import { formatTestResults } from './testResultFormatter';
import { testsEnabled } from '../../../session/sessionSettings';
import { getSessionLanguage } from '../../service/languageConfig';
import { getGuiSessionConfig } from '../../config/guiSessionConfig';

// runNote (study conditions only, study/studyRunNote.js): lines added under TEST RESULTS.
export const buildContextualPrompt = (userText, code, testResults, terminalOutput, taskDescription, session, recentInteractions, visualContext, runNote = '') => {
    const guiConfig = getGuiSessionConfig(session);
    // Web-dev sessions grade with page checks, not test_list rows.
    const formattedTests = formatTestResults(testResults, guiConfig ? null : session?.test_list);
    const recentLogs = terminalOutput.map(l => l.content).join('\n').slice(-500);
    const language = guiConfig ? guiConfig.phase : getSessionLanguage(Number(session?.id ?? session));
    let formattedCode = code;
    // InitialFinalCSS's student only ever edits the CSS file (enabledFiles: ['css']), so
    // the default formatting above would give the assistant CSS rules with no idea what
    // selectors/classes actually exist on the page - include the fixed markup it's styling
    // as read-only reference context (see getInitialFinalCSSSystemPrompt in systemPrompt.js).
    let markupReferenceSection = '';

    if (guiConfig) {
      try {
        const files = JSON.parse(code);
        formattedCode = guiConfig.enabledFiles
          .map(file => `--- ${file.toUpperCase()} ---\n${files[file] || ''}`)
          .join('\n\n');
        if (guiConfig.type === 'InitialFinalCSS') {
          markupReferenceSection = `\nPAGE MARKUP (read-only - the student cannot edit this):\n${files.html || ''}\n`;
        }
      } catch {
        formattedCode = code;
      }
    }

    const interactionSection = recentInteractions
      ? `\nRECENT UI INTERACTIONS (most recent last):\n${recentInteractions}\n`
      : '';

    // visualContext (viewport + DOM snapshot) is only ever populated when the session has
    // visualTraceEnabled on - see LivePreview.jsx / TelemetryContext.js. The screenshot
    // itself, if any, is attached as a separate multimodal message part (see ChatContext.js),
    // not inlined here as text. When aiVisualContextEnabled is also on and a screenshot is
    // actually available, skip this text block entirely - sending both the rendered image
    // AND an ~8000-character raw DOM dump on every message is redundant (the image already
    // shows the assistant the current layout) and was a real contributor to per-message
    // latency for image-bearing requests. Falls back to the DOM text if capture ever
    // produces no screenshot (e.g. a canvas-tainting cross-origin image), so the assistant
    // isn't left with neither.
    let visualSection = '';
    const imageAlreadyAttached = guiConfig?.aiVisualContextEnabled && !!visualContext?.screenshot;
    if (guiConfig?.visualTraceEnabled && visualContext && !imageAlreadyAttached) {
      const viewport = visualContext.viewport ? `${visualContext.viewport.width}x${visualContext.viewport.height}` : 'unknown';
      visualSection = `\nRENDERED VIEWPORT: ${viewport}\nRENDERED DOM (truncated):\n${visualContext.domSnapshot || '(unavailable)'}\n`;
    }

    return `
${userText}

--- CONTEXT ---
TASK INSTRUCTIONS:
${taskDescription || "No task description provided."}

CURRENT CODE:
\`\`\`${language}
${formattedCode}
\`\`\`
${markupReferenceSection}
TEST RESULTS:
${testsEnabled(session) ? formattedTests : "This exercise has no test cases - do not tell the student to run or pass tests."}${runNote ? `\n${runNote}` : ''}

TERMINAL OUTPUT:
${recentLogs}
${interactionSection}${visualSection}`;
};
